import { ReactNode, useEffect } from 'react'
import { navigate, PageProps } from 'gatsby'
import queryString from 'query-string'
import { useMount, useSetState } from 'ahooks'
import clsx from 'clsx'
import { Button, Error403, Paragraph } from 'components/common'
import config, { routes } from 'config'
import { useAuth0 } from 'lib/auth'

type State = {
  hasError: boolean
  errorType?: 'forbidden' | 'normal'
  errorMessage: string | ReactNode | null
}

type Auth0ErrorQueryString = {
  error?: string
  error_description?: string
}

const ErrorMessage = (): JSX.Element => (
  <>
    <div className={clsx('text-center', 'mt-40')}>
      <Paragraph>ログイン時に予期せぬエラーが発生しました。</Paragraph>
      <Paragraph>再度ログインしてください。</Paragraph>
      <Button
        type="link"
        className={clsx('mt-8')}
        onClick={() => navigate(routes.login)}
      >
        ログイン
      </Button>
    </div>
  </>
)

const Callback = ({ location }: PageProps<Queries.Query>): JSX.Element => {
  const [state, setState] = useSetState<State>({
    hasError: false,
    errorType: null,
    errorMessage: null,
  })
  const { isLoading, isAuthenticated, error, user } = useAuth0()

  useMount(() => {
    const { error, error_description: errorDescription } = queryString.parse(
      location.search
    ) as Auth0ErrorQueryString
    if (
      error === 'access_denied' &&
      /^user auth0|[w]+ is not part of the org_[\w]+ organization$/.test(
        errorDescription
      )
    ) {
      setState({
        hasError: true,
        errorType: 'forbidden',
        errorMessage: (
          <>
            <Paragraph>この組織にはログイン権限が有りません。</Paragraph>
            <Paragraph>
              組織およびメールアドレス、パスワードを確認のうえ、再度ログインしてください。
            </Paragraph>
          </>
        ),
      })
    } else if (error) {
      setState({
        hasError: true,
        errorType: 'normal',
        errorMessage: <ErrorMessage />,
      })
    } else {
      // Do not anything
    }
  })

  useEffect(() => {
    const { hasError } = state

    if (hasError) {
      return // すでにエラーハンドリングされている場合には何もしない
    } else if (isLoading) {
      // Do not anything
    } else if (isAuthenticated) {
      if (user && user.orgId) {
        if (config.auth0.observers.some((org) => org.id === user.orgId)) {
          navigate('/calendar')
        }
      }
    } else if (isAuthenticated === false || error) {
      setState({
        hasError: true,
        errorType: 'normal',
        errorMessage: <ErrorMessage />,
      })
    } else {
      // Do not anything
    }
  }, [error, isAuthenticated, isLoading, setState, state, user])

  if (state.hasError) {
    const { errorType, errorMessage } = state
    switch (errorType) {
      case 'forbidden':
        return <Error403 message={errorMessage} />
      case 'normal':
        return <>{errorMessage}</>
      default:
        // Do not anything
        break
    }
  }

  return <Paragraph>Loading...</Paragraph>
}

export default Callback
